/**
 * Core-100 R2 — CORE-N: the page a reference opens from the link the candidate sent them.
 *
 * No account is needed. They may answer in their own words — recorded as THEIR attestation, attributed
 * to the name they give — or decline to be a reference at all, which removes their phone number and
 * e-mail from the candidate's record. The link works once; the database decides (`submit_reference_response`,
 * `withdraw_as_reference`).
 */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';

export function tokenFromHash(hash: string): string | null {
  const t = hash.replace(/^#/, '').trim();
  return /^[0-9a-f]{48}$/.test(t) ? t : null;
}

export default function ReferenceRespond() {
  const token = tokenFromHash(typeof window !== 'undefined' ? window.location.hash : '');
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [response, setResponse] = useState('');
  const [recommend, setRecommend] = useState<boolean | null>(null);
  const [done, setDone] = useState<null | 'responded' | 'withdrew'>(null);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return <p className="p-6 text-sm text-muted-foreground" data-testid="reference-link-invalid">This link is not complete. Ask the person who sent it for a new one.</p>;
  }
  if (done) {
    return (
      <p className="p-6 text-sm" data-testid="reference-done">
        {done === 'responded' ? 'Thank you — your response was recorded in your own words.' : 'Done — you are no longer listed with your contact details.'}
      </p>
    );
  }

  const fail = (message: string) => setError(message.includes('REFERENCE_LINK_INVALID')
    ? 'This link has expired or was already used.' : 'That did not go through. Try again.');

  const submit = async () => {
    setError(null);
    const { error: err } = await supabase.rpc('submit_reference_response' as never, {
      p_token: token, p_author_name: name.trim(), p_relationship: relationship.trim() || null,
      p_response: response.trim(), p_would_recommend: recommend,
    } as never);
    if (err) { fail(err.message); return; }
    setDone('responded');
  };
  const withdraw = async () => {
    setError(null);
    const { error: err } = await supabase.rpc('withdraw_as_reference' as never, { p_token: token } as never);
    if (err) { fail(err.message); return; }
    setDone('withdrew');
  };

  return (
    <div className="container mx-auto max-w-xl p-4">
      <Card>
        <CardHeader>
          <CardTitle>You were listed as a reference</CardTitle>
          <CardDescription>Answer in your own words, or say you would rather not be a reference. Either way, it is your choice.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1"><Label htmlFor="ref-name">Your name</Label><Input id="ref-name" value={name} onChange={(e) => setName(e.target.value.slice(0, 120))} /></div>
          <div className="space-y-1"><Label htmlFor="ref-rel">How you know them (optional)</Label><Input id="ref-rel" value={relationship} onChange={(e) => setRelationship(e.target.value.slice(0, 120))} /></div>
          <div className="space-y-1"><Label htmlFor="ref-text">Your response</Label><Textarea id="ref-text" value={response} onChange={(e) => setResponse(e.target.value.slice(0, 4000))} /></div>
          <div className="flex gap-2 text-sm" role="radiogroup" aria-label="Would you recommend them?">
            {([[true, 'Would recommend'], [false, 'Would not recommend'], [null, 'Prefer not to say']] as const).map(([v, l]) => (
              <Button key={l} type="button" size="sm" variant={recommend === v ? 'default' : 'outline'} role="radio" aria-checked={recommend === v} onClick={() => setRecommend(v)}>{l}</Button>
            ))}
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <div className="flex flex-wrap justify-between gap-2">
            <Button onClick={() => void submit()} disabled={!name.trim() || !response.trim()}>Send my response</Button>
            <Button variant="ghost" onClick={() => void withdraw()}>I would rather not be a reference</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
